// Super Admin shell. Same architecture as the Client Admin layout
// (sidebar + sticky header + outlet). The sidebar groups nav items
// into named sections (Overview / Platform / Support) so the seven
// items stay legible as we add more.
import { useEffect, useState } from "react"
import { Link, NavLink, Outlet, useLocation } from "react-router-dom"
import { PageTransition } from "@/components/ui/page-transition"
import { SuperAdminHeader } from "@/components/layout/SuperAdminHeader"
import {
  LayoutDashboard,
  Users,
  Layers,
  Tag,
  Receipt,
  LifeBuoy,
  Globe2,
  ClipboardList,
} from "lucide-react"
import { useAuthStore } from "@/stores/auth"
import { BrandLogo } from "@/components/BrandLogo"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"

type SuperNavItem = {
  to: string
  label: string
  icon: typeof LayoutDashboard
  // When true, render the live super-action-center count as a badge.
  showActionBadge?: boolean
}

// Grouped nav. Each group has a header label rendered as an `text-overline`
// chip in the sidebar.
const navGroups: { label: string; items: SuperNavItem[] }[] = [
  {
    label: "Overview",
    items: [
      { to: "/super/dashboard", label: "Dashboard", icon: LayoutDashboard },
      {
        // Dedicated page (not a dashboard anchor) so its active state
        // doesn't collide with the Dashboard nav item.
        to: "/super/action-center",
        label: "Action Center",
        icon: ClipboardList,
        showActionBadge: true,
      },
      { to: "/super/clients", label: "Clients", icon: Users },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/super/subscriptions", label: "Subscriptions", icon: Layers },
      { to: "/super/coupons", label: "Coupons", icon: Tag },
      { to: "/super/billing", label: "Billing", icon: Receipt },
      { to: "/super/masters", label: "Masters", icon: Globe2 },
    ],
  },
  {
    label: "Support",
    items: [{ to: "/super/support", label: "Support Tickets", icon: LifeBuoy }],
  },
]

function getInitials(input: string | undefined): string {
  if (!input) return "SA"
  const parts = input.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function SuperLayout() {
  const user = useAuthStore((s) => s.user)
  const location = useLocation()

  // Action Center badge — sum of unresolved tickets + overdue invoices +
  // new clients this week. Polled on mount, route change, and every 60s.
  const [actionCount, setActionCount] = useState(0)
  useEffect(() => {
    let cancelled = false
    const fetchCount = () => {
      api
        .get<{ data: { total: number } }>("/super/action-center/count")
        .then((res) => {
          if (!cancelled) setActionCount(res.data.data.total)
        })
        .catch(() => {
          /* non-fatal */
        })
    }
    fetchCount()
    const id = window.setInterval(fetchCount, 60_000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)]">
      <aside className="sidebar-light fixed inset-y-0 left-0 z-30 w-64 border-r border-[var(--color-border)] flex flex-col">
        {/* Logo */}
        <div className="px-5 py-5">
          <Link
            to="/super/dashboard"
            className="font-display inline-flex items-center gap-2.5"
          >
            <BrandLogo size="sidebar" />
          </Link>
        </div>

        {/* Grouped nav */}
        <nav className="flex-1 px-3 pb-3 space-y-3 overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.label}>
              <p className="text-overline px-3.5 pt-2 pb-1.5">{group.label}</p>
              <div className="space-y-0.5">
                {group.items.map(({ to, label, icon: Icon, showActionBadge }) => {
                  const badge =
                    showActionBadge && actionCount > 0 ? actionCount : null
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) =>
                        cn(
                          "nav-item group flex items-center gap-3 rounded-full px-3.5 py-2 text-sm font-medium",
                          isActive
                            ? "is-active bg-[var(--color-accent)] text-white shadow-[var(--shadow-sm)]"
                            : "text-[color:var(--color-text-secondary)] hover:bg-[var(--color-accent-light)] hover:text-[color:var(--color-accent-hover)]",
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon
                            className={cn(
                              "nav-icon h-5 w-5 shrink-0 transition-colors",
                              isActive
                                ? "text-white"
                                : "text-[color:var(--color-text-muted)] group-hover:text-[color:var(--color-accent-hover)]",
                            )}
                            strokeWidth={1.5}
                          />
                          <span className="truncate">{label}</span>
                          {badge != null ? (
                            <span
                              className={cn(
                                "ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums",
                                isActive
                                  ? "bg-white/20 text-white"
                                  : "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300",
                              )}
                            >
                              {badge > 99 ? "99+" : badge}
                            </span>
                          ) : null}
                        </>
                      )}
                    </NavLink>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Minimal user block — same shape as the Client sidebar. Sign out,
            dark mode, and self-profile actions all live in the header
            dropdown so they aren't duplicated here. */}
        <div className="px-3 pb-4 pt-3 border-t border-[var(--color-border)]">
          <Link
            to="/super/profile"
            className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-[var(--color-accent-light)]"
            title="View profile"
          >
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-semibold"
              style={{
                background: "var(--color-accent-light)",
                color: "var(--color-accent-hover)",
              }}
            >
              {getInitials(user?.fullName ?? user?.email)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-body font-semibold text-foreground">
                {user?.fullName ?? user?.email ?? "Super Admin"}
              </div>
              <div className="truncate text-caption mt-0.5">
                {user?.email ?? "Control plane"}
              </div>
            </div>
          </Link>
        </div>
      </aside>

      <main className="ml-64 min-h-screen">
        <SuperAdminHeader />
        <div className="p-8 max-w-7xl mx-auto">
          <PageTransition key={location.pathname}>
            <Outlet />
          </PageTransition>
        </div>
      </main>
    </div>
  )
}
